import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getHeaders = (body) => {
  const isFormData = body instanceof FormData;

  const headers = {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    Lang: i18n.language,
  };

  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  return headers;
};

export const fetchLeasingEntries = createAsyncThunk(
  "leasingEntries/fetchAll",
  async ({ page = 1, filters = {} }, { rejectWithValue }) => {
    try {
      let url = `${BASE_URL}/leasing-request/entries?page=${page}`;

      if (filters.reference_number) {
        url += `&reference_number=${encodeURIComponent(filters.reference_number)}`;
      }
      if (filters.desc) {
        url += `&desc=${encodeURIComponent(filters.desc)}`;
      }
      if (filters.from_date) {
        url += `&from_date=${encodeURIComponent(filters.from_date)}`;
      }
      if (filters.to_date) {
        url += `&to_date=${encodeURIComponent(filters.to_date)}`;
      }
      if (filters.account_id) {
        url += `&account_id=${encodeURIComponent(filters.account_id)}`;
      }

      const response = await axios.get(url, {
        headers: getHeaders(),
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load entries list";
      return rejectWithValue(message);
    }
  }
);

export const fetchLeasingEntryRecord = createAsyncThunk(
  "leasingEntries/fetchRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/leasing-request/entries/${id}`, {
        headers: getHeaders(),
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load entry record";
      return rejectWithValue(message);
    }
  }
);

const appendEntryFormData = (formData) => {
  const data = new FormData();
  
  if (formData.entry_date) data.append("entry_date", formData.entry_date);
  if (formData.desc) data.append("desc", formData.desc);

  // Append items array
  if (formData.items && formData.items.length > 0) {
    formData.items.forEach((item, index) => {
      if (item.account_id) data.append(`items[${index}][account_id]`, item.account_id);
      if (item.debit !== undefined && item.debit !== null && item.debit !== "") {
        data.append(`items[${index}][debit]`, item.debit);
      }
      if (item.credit !== undefined && item.credit !== null && item.credit !== "") {
        data.append(`items[${index}][credit]`, item.credit);
      }
      if (item.desc) data.append(`items[${index}][desc]`, item.desc);
    });
  }

  // Append attachments array
  if (formData.attachments && formData.attachments.length > 0) {
    formData.attachments.forEach((file) => {
      data.append("attachments[]", file);
    });
  }

  return data;
};

export const addLeasingEntry = createAsyncThunk(
  "leasingEntries/add",
  async (formData, { rejectWithValue }) => {
    try {
      const data = appendEntryFormData(formData);
      const response = await axios.post(`${BASE_URL}/leasing-request/entries`, data, {
        headers: getHeaders(data),
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add entry";
      return rejectWithValue(message);
    }
  }
);

export const updateLeasingEntry = createAsyncThunk(
  "leasingEntries/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const data = appendEntryFormData(formData);
      const response = await axios.post(`${BASE_URL}/leasing-request/entries/${id}`, data, {
        headers: getHeaders(data),
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update entry";
      return rejectWithValue(message);
    }
  }
);

export const deleteLeasingEntry = createAsyncThunk(
  "leasingEntries/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${BASE_URL}/leasing-request/entries/${id}`, {
        headers: getHeaders(),
      });
      return { id, message: response.data.message || "Deleted successfully" };
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete entry";
      return rejectWithValue(message);
    }
  }
);

export const fetchAccountsForDropdown = createAsyncThunk(
  "leasingEntries/fetchAccounts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/settings/debit-credit-accounts`, {
        headers: getHeaders(),
      });
      return response.data.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load accounts";
      return rejectWithValue(message);
    }
  }
);

const LeasingEntriesSlice = createSlice({
  name: "leasingEntries",
  initialState: {
    entries: [],
    entryRecord: null,
    pagination: null,
    accounts: [],
    isLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeasingEntries.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeasingEntries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.entries = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
      })
      .addCase(fetchLeasingEntries.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchLeasingEntryRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeasingEntryRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.entryRecord = action.payload;
      })
      .addCase(fetchLeasingEntryRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addLeasingEntry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addLeasingEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "Added successfully";
      })
      .addCase(addLeasingEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateLeasingEntry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateLeasingEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "Updated successfully";
      })
      .addCase(updateLeasingEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteLeasingEntry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteLeasingEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.entries = state.entries.filter((item) => item.id !== action.payload.id);
        state.success = action.payload.message || "Deleted successfully";
      })
      .addCase(deleteLeasingEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchAccountsForDropdown.fulfilled, (state, action) => {
        state.accounts = action.payload;
      });
  },
});

export const { clearState } = LeasingEntriesSlice.actions;
export default LeasingEntriesSlice.reducer;

